/**
 * Restaurant Settings Tab Component
 * Internal settings for restaurant configuration - Super Admin only
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { SuperAdminOnboardingData } from '@/types/super-admin-onboarding';
import { OnboardingStatus } from '@/types/onboarding';
import { Save, AlertCircle, Shield } from 'lucide-react';
import WebhookCaptureCard from '../WebhookCaptureCard';

interface RestaurantSettingsTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const RestaurantSettingsTab: React.FC<RestaurantSettingsTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState({
    organisation_id: data.organisation_id || '',
    restaurant_id: data.restaurant_id || '',
    logo_url: data.logo_url || '',
    template_restaurant_name: data.template_restaurant_name || data.organisation_name || '',
    webhook_secret: data.webhook_secret || '',
    restaurant_api_key: data.restaurant_api_key || '',
    google_oauth_client_id: data.google_oauth_client_id || '',
    commission_rate: data.commission_rate?.toString() || '',
    ubereats_commission_rate: data.ubereats_commission_rate?.toString() || '',
    pickup_commission_rate: data.pickup_commission_rate?.toString() || '',
    stripe_customer_id: data.stripe_customer_id || '',
    opt_in_checkbox_id: data.opt_in_checkbox_id || '',
    implicit_consent_enabled: data.implicit_consent_enabled || false,
    ordering_page_url: data.ordering_page_url || '',
    review_page_url: data.review_page_url || ''
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Prepopulate template_restaurant_name with organisation_name if not set
  useEffect(() => {
    if (data.organisation_name && !data.template_restaurant_name && !formData.template_restaurant_name) {
      setFormData(prev => ({
        ...prev,
        template_restaurant_name: data.organisation_name
      }));
    }
  }, [data.organisation_name, data.template_restaurant_name]);

  const handleFieldChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required field validations
    if (!formData.organisation_id?.trim()) {
      newErrors.organisation_id = 'Organisation ID is required';
    }
    
    if (!formData.restaurant_id?.trim()) {
      newErrors.restaurant_id = 'Restaurant ID is required';
    }

    // URL validations
    const urlFields = ['logo_url', 'ordering_page_url', 'review_page_url'] as const;
    urlFields.forEach(field => {
      const value = formData[field];
      if (value && value.trim() && !value.match(/^https?:\/\/.+/)) {
        newErrors[field] = 'Please enter a valid URL starting with http:// or https://';
      }
    });

    // Commission rate validations
    const rateFields = ['commission_rate', 'ubereats_commission_rate', 'pickup_commission_rate'] as const;
    rateFields.forEach(field => {
      const value = formData[field];
      if (value && (isNaN(parseFloat(value)) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
        newErrors[field] = 'Please enter a valid percentage between 0 and 100';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAll = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving.',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      // Prepare form data with proper type conversion
      const processedData = { ...formData };
      
      // Convert string values to numbers for commission rates
      ['commission_rate', 'ubereats_commission_rate', 'pickup_commission_rate'].forEach(field => {
        const value = processedData[field as keyof typeof processedData];
        if (typeof value === 'string' && value) {
          processedData[field as keyof typeof processedData] = parseFloat(value);
        } else if (value === '') {
          processedData[field as keyof typeof processedData] = null;
        }
      });

      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(processedData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const changedFields = Object.entries(processedData).filter(([field, value]) => {
          const originalValue = data[field as keyof SuperAdminOnboardingData];
          return originalValue !== value;
        });
        
        if (changedFields.length === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
          return;
        }
        
        const promises = changedFields.map(([field, value]) => 
          onFieldUpdate(field as keyof SuperAdminOnboardingData, value)
        );
        
        await Promise.all(promises);
        
        toast({
          title: 'Success',
          description: `Updated ${changedFields.length} field(s) successfully`
        });
      }
    } catch (error) {
      console.error('Error saving restaurant settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save restaurant settings.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900">Internal Restaurant Settings</h4>
            <p className="text-sm text-yellow-700 mt-1">
              These settings are for internal use only and are not visible to the restaurant owner during normal onboarding.
              These fields will be used when creating the restaurant profile.
            </p>
          </div>
        </div>
      </div>

      {/* Basic Restaurant Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Restaurant Profile Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Critical ID Fields */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-red-900 mb-1">Critical Restaurant Identifiers</h4>
                <p className="text-sm text-red-700 mb-3">
                  These IDs are determined externally and must be set manually. Never auto-generate these values as they will be incorrect.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="organisation_id">Organisation ID *</Label>
                    <Input
                      id="organisation_id"
                      value={formData.organisation_id}
                      onChange={(e) => handleFieldChange('organisation_id', e.target.value)}
                      placeholder="Enter organisation ID"
                      className={errors.organisation_id ? 'border-red-500' : ''}
                      required
                    />
                    {errors.organisation_id && (
                      <p className="text-sm text-red-600">{errors.organisation_id}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      External organisation identifier (required for restaurant creation)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="restaurant_id">Restaurant ID *</Label>
                    <Input
                      id="restaurant_id"
                      value={formData.restaurant_id}
                      onChange={(e) => handleFieldChange('restaurant_id', e.target.value)}
                      placeholder="Enter restaurant ID"
                      className={errors.restaurant_id ? 'border-red-500' : ''}
                      required
                    />
                    {errors.restaurant_id && (
                      <p className="text-sm text-red-600">{errors.restaurant_id}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      External restaurant identifier (required for restaurant creation)
                    </p>
                  </div>
                </div>

                <WebhookCaptureCard
                  userId={data.user_id}
                  onConfirmed={({ organisation_id, restaurant_id }) => {
                    // The capture endpoint has already written these to the onboarding
                    // record; mirror them into the form so a later save doesn't
                    // overwrite them with the stale values still held in state.
                    setFormData(prev => ({
                      ...prev,
                      organisation_id,
                      restaurant_id: restaurant_id || prev.restaurant_id
                    }));
                    setErrors(prev => ({ ...prev, organisation_id: '', restaurant_id: '' }));
                  }}
                />
              </div>
            </div>
          </div>

          {/* Restaurant Branding */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                value={formData.logo_url}
                onChange={(e) => handleFieldChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
                className={errors.logo_url ? 'border-red-500' : ''}
              />
              {errors.logo_url && (
                <p className="text-sm text-red-600">{errors.logo_url}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Public URL to the restaurant logo image
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template_restaurant_name">Restaurant Name for SMS Messages</Label>
              <Input
                id="template_restaurant_name"
                value={formData.template_restaurant_name}
                onChange={(e) => handleFieldChange('template_restaurant_name', e.target.value)}
                placeholder={data.organisation_name || "e.g., Joe's Pizza"}
              />
              <p className="text-xs text-muted-foreground">
                Brand name used in SMS templates (e.g., "Joe's Pizza" instead of "Joe's Pizza - Wellington")
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ordering_page_url">Ordering Page URL</Label>
              <Input
                id="ordering_page_url"
                value={formData.ordering_page_url}
                onChange={(e) => handleFieldChange('ordering_page_url', e.target.value)}
                placeholder="https://example.com/order"
                className={errors.ordering_page_url ? 'border-red-500' : ''}
              />
              {errors.ordering_page_url && (
                <p className="text-sm text-red-600">{errors.ordering_page_url}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="review_page_url">Review Page URL</Label>
              <Input
                id="review_page_url"
                value={formData.review_page_url}
                onChange={(e) => handleFieldChange('review_page_url', e.target.value)}
                placeholder="https://example.com/reviews"
                className={errors.review_page_url ? 'border-red-500' : ''}
              />
              {errors.review_page_url && (
                <p className="text-sm text-red-600">{errors.review_page_url}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API & Integration Settings */}
      <Card>
        <CardHeader>
          <CardTitle>API & Integration Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhook_secret">Webhook Secret</Label>
            <Input
              id="webhook_secret"
              value={formData.webhook_secret}
              onChange={(e) => handleFieldChange('webhook_secret', e.target.value)}
              placeholder="webhook_xxxxxxxx"
            />
            <p className="text-xs text-muted-foreground">
              Secret key for webhook authentication
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="restaurant_api_key">Ordering Dashboard API Key</Label>
            <Input
              id="restaurant_api_key"
              value={formData.restaurant_api_key}
              onChange={(e) => handleFieldChange('restaurant_api_key', e.target.value)}
              placeholder="api_xxxxxxxx"
            />
            <p className="text-xs text-muted-foreground">
              API key for Cloudwaitress ordering dashboard integration
              <br />
              <strong>Note:</strong> Stored as 'restaurant_api_key' but sent as 'api_key' to restaurant creation API
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="google_oauth_client_id">Google OAuth Client ID</Label>
            <Input
              id="google_oauth_client_id"
              value={formData.google_oauth_client_id}
              onChange={(e) => handleFieldChange('google_oauth_client_id', e.target.value)}
              placeholder="xxxxxxxx-xxxxxxxxxxxxxxxxxxxxxxxx.apps.googleusercontent.com"
            />
            <p className="text-xs text-muted-foreground">
              Google OAuth Client ID for authentication and integration
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Commission Rates */}
      <Card>
        <CardHeader>
          <CardTitle>Commission Rates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="commission_rate">Commission Rate (%)</Label>
              <Input
                id="commission_rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.commission_rate}
                onChange={(e) => handleFieldChange('commission_rate', e.target.value)}
                className={errors.commission_rate ? 'border-red-500' : ''}
              />
              {errors.commission_rate && (
                <p className="text-sm text-red-600">{errors.commission_rate}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ubereats_commission_rate">UberEats Commission Rate (%)</Label>
              <Input
                id="ubereats_commission_rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.ubereats_commission_rate}
                onChange={(e) => handleFieldChange('ubereats_commission_rate', e.target.value)}
                className={errors.ubereats_commission_rate ? 'border-red-500' : ''}
              />
              {errors.ubereats_commission_rate && (
                <p className="text-sm text-red-600">{errors.ubereats_commission_rate}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pickup_commission_rate">Pickup Commission Rate (%)</Label>
              <Input
                id="pickup_commission_rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.pickup_commission_rate}
                onChange={(e) => handleFieldChange('pickup_commission_rate', e.target.value)}
                className={errors.pickup_commission_rate ? 'border-red-500' : ''}
              />
              {errors.pickup_commission_rate && (
                <p className="text-sm text-red-600">{errors.pickup_commission_rate}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing & Marketing */}
      <Card>
        <CardHeader>
          <CardTitle>Billing & Marketing Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="stripe_customer_id">Stripe Customer ID</Label>
            <Input
              id="stripe_customer_id"
              value={formData.stripe_customer_id}
              onChange={(e) => handleFieldChange('stripe_customer_id', e.target.value)}
              placeholder="cus_xxxxxxxx"
            />
            <p className="text-xs text-muted-foreground">
              Stripe customer ID for SMS billing (optional)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="opt_in_checkbox_id">Marketing Opt-in Checkbox ID</Label>
            <Input
              id="opt_in_checkbox_id"
              value={formData.opt_in_checkbox_id}
              onChange={(e) => handleFieldChange('opt_in_checkbox_id', e.target.value)}
              placeholder="e.g., oSR52NcpL"
            />
            <p className="text-xs text-muted-foreground">
              ID of the checkout form checkbox used for marketing opt-in (optional)
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                id="implicit_consent_enabled"
                checked={formData.implicit_consent_enabled}
                onCheckedChange={(checked) => handleFieldChange('implicit_consent_enabled', checked)}
              />
              <Label htmlFor="implicit_consent_enabled">
                Enable implicit consent for marketing
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              When enabled, all customers will be automatically opted into marketing unless they have previously opted out from this organisation.
            </p>
            <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-md">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <p className="text-xs text-yellow-800">
                Important: Ensure the restaurant has appropriate consent collection and opt-out processes in place before enabling this feature.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <Button 
          onClick={handleSaveAll}
          disabled={saving}
          className="min-w-32"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Restaurant Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default RestaurantSettingsTab;